import React, { useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  Card,
  EmptyState,
  Input,
  PageHeader,
  SectionHeader,
  Skeleton,
  Tag,
  Textarea,
} from '../components';
import { ThemeToggle } from '../theme/ThemeToggle';
import { useThemeMode } from '../theme/useTheme';

const Page = styled.div`
  min-height: 100vh;
  background: var(--color-bg);
  color: var(--color-text);
  padding: var(--space-8) var(--space-6) var(--space-16);
`;

const Container = styled.div`
  max-width: 1120px;
  margin: 0 auto;
`;

const Section = styled.section`
  margin-top: var(--space-10);
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  align-items: center;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: var(--space-4);
`;

const SwatchGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: var(--space-3);
`;

const Swatch = styled.div<{ $bg: string }>`
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-3);
  border-radius: var(--radius-md);
  background: ${(p) => p.$bg};
  border: 1px solid var(--color-border);
  min-height: 72px;
`;

const SwatchLabel = styled.span`
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  font-family: var(--font-mono);
`;

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

const SparkleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
  </svg>
);

const FolderIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 7a2 2 0 0 1 2-2h4l2 3h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" />
  </svg>
);

export const Catalog: React.FC = () => {
  const { mode } = useThemeMode();
  const [inputValue, setInputValue] = useState('');

  return (
    <div className="ds-root">
      <Page>
        <Container>
          <PageHeader
            title="Design System"
            subtitle={`Tokens and primitives — currently in ${mode} mode. Toggle to preview both.`}
            breadcrumb={<span>Internal · Catalog</span>}
            actions={
              <>
                <Tag variant="accent" size="md">
                  v0.1
                </Tag>
                <ThemeToggle />
              </>
            }
          />

          <Section>
            <SectionHeader
              title="Color tokens"
              description="Surface, text, accent, and semantic colors driven by CSS variables."
            />
            <Card padding="md">
              <SwatchGrid>
                <Swatch $bg="var(--color-bg)">
                  <SwatchLabel>bg</SwatchLabel>
                </Swatch>
                <Swatch $bg="var(--color-surface)">
                  <SwatchLabel>surface</SwatchLabel>
                </Swatch>
                <Swatch $bg="var(--color-surface-subtle)">
                  <SwatchLabel>surface-subtle</SwatchLabel>
                </Swatch>
                <Swatch $bg="var(--color-surface-inset)">
                  <SwatchLabel>surface-inset</SwatchLabel>
                </Swatch>
                <Swatch $bg="var(--color-accent)">
                  <SwatchLabel style={{ color: 'var(--color-text-inverse)' }}>
                    accent
                  </SwatchLabel>
                </Swatch>
                <Swatch $bg="var(--color-accent-subtle)">
                  <SwatchLabel>accent-subtle</SwatchLabel>
                </Swatch>
                <Swatch $bg="var(--color-success-subtle)">
                  <SwatchLabel>success-subtle</SwatchLabel>
                </Swatch>
                <Swatch $bg="var(--color-warning-subtle)">
                  <SwatchLabel>warning-subtle</SwatchLabel>
                </Swatch>
                <Swatch $bg="var(--color-danger-subtle)">
                  <SwatchLabel>danger-subtle</SwatchLabel>
                </Swatch>
              </SwatchGrid>
            </Card>
          </Section>

          <Section>
            <SectionHeader
              title="Typography"
              description="Inter for UI, Fraunces for display headings."
            />
            <Card padding="lg">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-4xl)', fontWeight: 600, margin: 0 }}>
                  Coaching, on purpose.
                </h1>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-2xl)', fontWeight: 600, margin: 0 }}>
                  Build the day plan
                </h2>
                <p style={{ fontSize: 'var(--font-size-lg)', color: 'var(--color-text-secondary)', margin: 0 }}>
                  Section heading
                </p>
                <p style={{ fontSize: 'var(--font-size-base)', margin: 0 }}>
                  Body text — Inter 14px. Used for default UI copy across the
                  application.
                </p>
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-tertiary)', margin: 0 }}>
                  Caption — Inter 12px.
                </p>
              </div>
            </Card>
          </Section>

          <Section>
            <SectionHeader
              title="Buttons"
              description="Five variants, three sizes, with loading and icon support."
            />
            <Card padding="lg">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
                <Row>
                  <Button variant="primary">Primary</Button>
                  <Button variant="secondary">Secondary</Button>
                  <Button variant="ghost">Ghost</Button>
                  <Button variant="danger">Delete</Button>
                  <Button variant="ai" iconLeft={<SparkleIcon />}>
                    Generate with AI
                  </Button>
                </Row>
                <Row>
                  <Button variant="primary" size="sm">
                    Small
                  </Button>
                  <Button variant="primary" size="md">
                    Medium
                  </Button>
                  <Button variant="primary" size="lg">
                    Large
                  </Button>
                </Row>
                <Row>
                  <Button variant="primary" isLoading>
                    Saving
                  </Button>
                  <Button variant="secondary" disabled>
                    Disabled
                  </Button>
                  <Button
                    variant="secondary"
                    iconLeft={<SearchIcon />}
                  >
                    Search
                  </Button>
                </Row>
              </div>
            </Card>
          </Section>

          <Section>
            <SectionHeader
              title="Inputs"
              description="Text, textarea, and form states."
            />
            <Grid>
              <Card padding="md">
                <Input
                  label="Community name"
                  placeholder="Maria's Coaching"
                  fullWidth
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  helperText="Shown to clients in the app."
                />
              </Card>
              <Card padding="md">
                <Input
                  label="Search"
                  placeholder="Find a client…"
                  fullWidth
                  iconLeft={<SearchIcon />}
                />
              </Card>
              <Card padding="md">
                <Input
                  label="Email"
                  defaultValue="invalid-email"
                  fullWidth
                  error="Enter a valid email address."
                />
              </Card>
              <Card padding="md">
                <Input
                  label="Disabled"
                  defaultValue="Locked value"
                  fullWidth
                  disabled
                />
              </Card>
              <Card padding="md" style={{ gridColumn: '1 / -1' }}>
                <Textarea
                  label="AI guidelines"
                  placeholder="Tone, do's and don'ts for the AI coach…"
                  fullWidth
                  helperText="The AI uses this on every response."
                />
              </Card>
            </Grid>
          </Section>

          <Section>
            <SectionHeader
              title="Tags"
              description="Status and metadata pills."
            />
            <Card padding="lg">
              <Row>
                <Tag variant="neutral">Draft</Tag>
                <Tag variant="accent">AI generated</Tag>
                <Tag variant="success" withDot>
                  Live
                </Tag>
                <Tag variant="warning" withDot>
                  Pending review
                </Tag>
                <Tag variant="danger" withDot>
                  Failed
                </Tag>
                <Tag variant="info">Beta</Tag>
                <Tag variant="success" size="sm">
                  Sm
                </Tag>
              </Row>
            </Card>
          </Section>

          <Section>
            <SectionHeader
              title="Cards"
              description="Elevations and interactive surfaces."
            />
            <Grid>
              <Card elevation="none" padding="md">
                <strong>Elevation: none</strong>
                <p style={{ margin: '4px 0 0', color: 'var(--color-text-secondary)' }}>
                  Flush surface, only border.
                </p>
              </Card>
              <Card elevation="sm" padding="md">
                <strong>Elevation: sm</strong>
                <p style={{ margin: '4px 0 0', color: 'var(--color-text-secondary)' }}>
                  Subtle lift for cards.
                </p>
              </Card>
              <Card elevation="md" padding="md">
                <strong>Elevation: md</strong>
                <p style={{ margin: '4px 0 0', color: 'var(--color-text-secondary)' }}>
                  For emphasis, dialogs.
                </p>
              </Card>
              <Card elevation="lg" padding="md" interactive>
                <strong>Interactive</strong>
                <p style={{ margin: '4px 0 0', color: 'var(--color-text-secondary)' }}>
                  Hover to lift.
                </p>
              </Card>
            </Grid>
          </Section>

          <Section>
            <SectionHeader
              title="Empty state"
              description="Used when a list or surface has no content yet."
            />
            <EmptyState
              icon={<FolderIcon />}
              title="No customers yet"
              description="Invite your first client to start coaching. They'll appear here once they accept."
              actions={
                <>
                  <Button variant="primary">Invite a client</Button>
                  <Button variant="ghost">Learn how invites work</Button>
                </>
              }
            />
          </Section>

          <Section>
            <SectionHeader
              title="Skeleton"
              description="Loading placeholders that shimmer."
            />
            <Card padding="lg">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
                  <Skeleton variant="circle" width={40} height={40} />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <Skeleton width="40%" />
                    <Skeleton width="70%" />
                  </div>
                </div>
                <Skeleton variant="rect" height={120} />
              </div>
            </Card>
          </Section>
        </Container>
      </Page>
    </div>
  );
};
